// pages/SearchPage.jsx
// Fixed: images now use absolute URLs from API, full-size masonry grid

import { useRef, useEffect } from "react";
import { trackEvent } from "../utils/analytics";
import { useNavigate } from "react-router-dom";
import { Loader2, User } from "lucide-react";
import SearchBar from "../components/Search/SearchBar";
import Suggestions from "../components/Search/Suggestions";
import useSearch from "../hooks/useSearch";
import ShareButton from "../components/Post/ShareButton";

const SearchPage = ({ hideSearch = false }) => {
  const navigate = useNavigate();
  const loaderRef = useRef(null);

  const {
    query, isTyping, posts, users,
    loading, loadingMore, hasMore, error,
    handleQueryChange, loadMore, setIsTyping,
  } = useSearch();

  useEffect(() => {
    if (!isTyping && query) {
      trackEvent('search_query', { metadata: { query } });
    }
  }, [isTyping, query]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) loadMore();
      },
      { threshold: 0.5 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  const handleClear = () => { handleQueryChange(""); setIsTyping(false); };
  const handleSelect = (item) => { handleQueryChange(item); setIsTyping(false); };

  return (
    <div className={`${hideSearch ? "" : "min-h-screen"} bg-black text-white px-1.5 py-4`}>

      {/* Search Bar */}
      {!hideSearch && (
        <SearchBar query={query} onChange={handleQueryChange} onClear={handleClear} />
      )}

      {/* Suggestions — only while actively typing */}
      {!hideSearch && isTyping && (
        <Suggestions query={query} users={users} onSelect={handleSelect} />
      )}

      {/* Error */}
      {error && <p className="text-red-400 text-sm text-center mt-4">{error}</p>}

      {/* Search Results — after search submitted */}
      {(!isTyping || hideSearch) && (
        <>
          {!hideSearch && (
            <p className="text-white/30 text-xs uppercase tracking-wider mt-5 mb-3 px-1">
              {query ? `Results for "${query}"` : "Explore"}
            </p>
          )}

          {/* ✅ Users Section — Instagram style (only when searching) */}
          {query && users.length > 0 && !loading && (
            <div className="mb-5">
              <p className="text-white/30 text-xs uppercase tracking-wider px-1 mb-2">
                People
              </p>
              <div className="flex flex-col gap-1">
                {users.slice(0, 5).map((user) => (
                  <button
                    key={user.id}
                    onClick={() => navigate(`/profile/${user.username}`)}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl
                      hover:bg-white/5 active:bg-white/10 transition-colors
                      text-left w-full"
                  >
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-white/10
                      flex items-center justify-center shrink-0 ring-1 ring-white/10">
                      {(user.profile_icon || user.profile_picture) ? (
                        <img
                          src={user.profile_icon || user.profile_picture}
                          alt={user.username}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <User className="w-5 h-5 text-white/30" />
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex flex-col min-w-0">
                      <span className="text-white text-sm font-semibold truncate">
                        {user.username}
                      </span>
                      {(user.first_name || user.last_name) && (
                        <span className="text-white/40 text-xs truncate">
                          {[user.first_name, user.last_name].filter(Boolean).join(" ")}
                        </span>
                      )}
                      {user.bio && (
                        <span className="text-white/25 text-xs truncate mt-0.5">
                          {user.bio}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <div className="border-t border-white/5 mt-3" />
            </div>
          )}

          {/* Loading Skeleton */}
          {loading ? (
            <div className="columns-2 gap-1 space-y-1">
              {[...Array(6)].map((_, i) => (
                <div key={i}
                  className="break-inside-avoid bg-white/5 rounded-2xl
                    animate-pulse mb-1"
                  style={{ height: `${180 + (i % 3) * 60}px` }}
                />
              ))}
            </div>
          ) : posts.length === 0 && users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              {query ? (
                <>
                  <p className="text-white/20 text-lg">No results found</p>
                  <p className="text-white/10 text-sm">Try a different search</p>
                </>
              ) : (
                <>
                  <p className="text-white/20 text-lg">Start searching</p>
                  <p className="text-white/10 text-sm">Find posts and people</p>
                </>
              )}
            </div>
          ) : (
            /* ✅ Pinterest masonry grid — posts */
            posts.length > 0 && (
              <>
                {query && users.length > 0 && (
                  <p className="text-white/30 text-xs uppercase tracking-wider px-1 mb-3">
                    Posts
                  </p>
                )}
                <div className="columns-2 gap-1 space-y-1">
                  {posts.map((post, index) => (
                    <div
                      key={`${post.id}-${index}`}
                      onClick={() => { trackEvent('post_click', { postId: post.id }); navigate(`/post/${post.id}`); }}
                      className="break-inside-avoid rounded-2xl overflow-hidden
                        cursor-pointer group relative mb-1"
                    >
                      {/* Share icon — top-right corner, stops card navigation */}
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-1.5 right-1.5 z-10
                          bg-black/40 rounded-full"
                      >
                        <ShareButton postId={post.id} caption={post.caption} />
                      </div>
                      <img
                        src={post.image}
                        alt={post.caption || "post"}
                        className="w-full object-cover group-hover:scale-105
                          transition duration-300"
                        style={{ minHeight: "150px" }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40
                        transition-all duration-300 flex items-end p-3">
                        <div className="opacity-0 group-hover:opacity-100
                          transition-opacity duration-300 w-full">



                          {/* Author avatar + username — clickable */}
                          <div
                            className="flex items-center gap-2 mb-1 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              const uname = post.username || post.author?.username;
                              if (uname) navigate(`/profile/${uname}`);
                            }}
                          >
                            {(post.profile_icon || post.author?.profile_picture) ? (
                              <img
                                src={post.profile_icon || post.author?.profile_picture}
                                alt=""
                                className="w-5 h-5 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-5 h-5 rounded-full bg-white/20
                                flex items-center justify-center">
                                <User className="w-3 h-3 text-white/50" />
                              </div>
                            )}
                            <span className="text-white/70 text-xs font-medium hover:text-white transition">
                              {post.username || post.author?.username}
                            </span>
                          </div>
                          {post.caption && (
                            <p className="text-white/80 text-xs line-clamp-2">
                              {post.caption}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )
          )}

          {/* Infinite scroll loader */}
          {hasMore && (
            <div ref={loaderRef} className="flex justify-center py-8">
              {loadingMore && (
                <Loader2 className="w-6 h-6 animate-spin text-white/30" />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SearchPage;